import { h, Component, forwardRef } from 'preact'
import { View, throwError } from 'cerebral'
import PropTypes from 'prop-types'
import { ControllerContext } from './context'

class BaseComponent extends Component {
  constructor(dependencies, mergeProps, props, controller, name) {
    super(props)
    if (!controller) {
      throwError(
        'Can not find controller, did you remember to use the Container component? Read more at: http://cerebraljs.com/docs/api/components.html#preact-(beta)'
      )
    }

    this.onUpdate = this.onUpdate.bind(this)
    this.view = new View({
      dependencies,
      mergeProps,
      props,
      controller,
      displayName: name,
      onUpdate: this.onUpdate,
    })
    this.view.mount()
  }

  /*
    We only allow forced render by change of props passed
    or Container tells it to render
  */
  shouldComponentUpdate(nextProps) {
    return this.view.onPropsUpdate(this.props, nextProps)
  }

  /*
    We have to update any usage of "get" when the component mounts
  */
  componentDidMount() {
    if (this.view.dynamicDependencies.length) {
      this.view.update(this.props)
    }
  }

  /*
    We have to update any usage of "get" when the component updates
  */
  componentDidUpdate() {
    if (this.view.dynamicDependencies.length) {
      this.view.update(this.props)
    }
  }

  /*
    Unregister with existing state dependencies
  */
  componentWillUnmount() {
    this.view.unMount()
  }

  onUpdate(stateChanges, force) {
    this.view.updateFromState(stateChanges, this.props, force)
    this.forceUpdate()
  }
}

export default function HOC(dependencies, mergeProps, Component) {
  class CerebralComponent extends BaseComponent {
    constructor(props, context) {
      // Support both Preact 8 and 10 context
      super(
        dependencies,
        mergeProps,
        props,
        props.controller || (context && context.controller),
        Component.displayName || Component.name
      )

      this.wrappedComponent = null
      this.setWrappedComponent = (instance) => {
        this.wrappedComponent = instance
      }
    }

    toJSON() {
      return this.view._displayName
    }

    render() {
      return this.view.render(this.props, (componentProps) => {
        // Capture reference for class components
        if (Component.prototype && Component.prototype.render) {
          return h(Component, {
            ...componentProps,
            ref: this.setWrappedComponent,
          })
        }

        return h(Component, componentProps)
      })
    }
  }

  CerebralComponent.displayName = `CerebralWrapping_${
    Component.displayName || Component.name
  }`

  CerebralComponent.contextTypes = {
    controller: PropTypes.object,
  }

  const WrappedComponent =
    typeof forwardRef === 'function'
      ? forwardRef((props, ref) => {
          const setCerebralComponentRef = (instance) => {
            if (ref) {
              if (typeof ref === 'function') {
                ref(instance)
              } else {
                ref.current = instance
              }
            }
          }

          if (ControllerContext && ControllerContext.Consumer) {
            return h(ControllerContext.Consumer, null, function (controller) {
              if (!controller) {
                throwError(
                  'Can not find controller, did you remember to use the Container component? Read more at: http://cerebraljs.com/docs/api/components.html#preact-(beta)'
                )
              }

              return h(CerebralComponent, {
                ...props,
                controller,
                ref: setCerebralComponentRef,
              })
            })
          }

          return h(CerebralComponent, {
            ...props,
            ref: setCerebralComponentRef,
          })
        })
      : function (props) {
          if (ControllerContext && ControllerContext.Consumer) {
            return h(ControllerContext.Consumer, null, function (controller) {
              if (!controller) {
                throwError(
                  'Can not find controller, did you remember to use the Container component? Read more at: http://cerebraljs.com/docs/api/components.html#preact-(beta)'
                )
              }

              return h(CerebralComponent, {
                ...props,
                controller,
              })
            })
          }

          return h(CerebralComponent, props)
        }

  WrappedComponent.toJSON = () => Component.displayName || Component.name

  WrappedComponent.getWrappedInstance = (ref) => {
    if (ref && ref.wrappedComponent) {
      return ref.wrappedComponent
    }
    return null
  }

  if (Component.displayName) {
    WrappedComponent.displayName = `ConnectWrapping_${Component.displayName}`
  }

  return WrappedComponent
}
